import {reactive, readonly} from "vue";
import {DateTime} from "luxon";
// @ts-ignore
import {useCookies} from "@vueuse/integrations/useCookies";

/**
 * Cookie names used in the application
 */
export const COOKIE_NAMES = {
    USER_PREFERENCES: "user_preferences",
};

/**
 * Available user preference keys
 */
export const USER_PREFERENCES_KEYS = {
    TIME_OF_DAY_VIEW: "TIME_OF_DAY_VIEW",
    LOCATION_MACHINES_TABLE_COLUMNS: "LOCATION_MACHINES_TABLE_COLUMNS",
} as const;

export type LocationMachinesTablePreferences = {
    last_usage: boolean;
    last_update: boolean;
    install_date: boolean;
    group_name: boolean;
    sortDescOrder: boolean;
    appliedSort: string;
};

/**
 * Type definition for user preferences
 */
export type UserPreferences = {
    [USER_PREFERENCES_KEYS.TIME_OF_DAY_VIEW]: string | null;
    [USER_PREFERENCES_KEYS.LOCATION_MACHINES_TABLE_COLUMNS]: LocationMachinesTablePreferences;
};

/**
 * Default values for user preferences
 */
export const DEFAULT_PREFERENCES: UserPreferences = {
    TIME_OF_DAY_VIEW: null,
    LOCATION_MACHINES_TABLE_COLUMNS: {
        last_usage: false,
        last_update: true,
        install_date: true,
        group_name: true,
        sortDescOrder: false,
        appliedSort: "sort-machine-number",
    },
};

/**
 * Cookie configuration for user preferences
 */
export const COOKIE_CONFIG = {
    path: '/',
    expirationYears: 1,
};

/**
 * Type representing the return value of the usePreferences composable
 */
export type UsePreferencesType = ReturnType<typeof usePreferences>;

/**
 * Composable for managing user preferences
 *
 * Provides functions to get, set, and persist user preferences in cookies
 *
 * @returns Object containing preference state and utility functions
 */
export function usePreferences() {
    const cookies = useCookies<UserPreferences>([COOKIE_NAMES.USER_PREFERENCES]);

    // Initialize the state from cookies or use defaults
    const state = reactive<UserPreferences>(
        cookies.get(COOKIE_NAMES.USER_PREFERENCES) || {...DEFAULT_PREFERENCES}
    );

    /**
     * Saves the current preferences to cookies
     */
    const savePreferences = () => {
        cookies.set(COOKIE_NAMES.USER_PREFERENCES, state, {
            path: COOKIE_CONFIG.path,
            expires: DateTime.now().plus({years: COOKIE_CONFIG.expirationYears}).toJSDate(),
        });
    };

    /**
     * Sets a preference value and persists it to cookies
     *
     * @param key - The preference key to set
     * @param value - The value to set for the preference
     */
    const setPreference = <K extends keyof UserPreferences>(
        key: K,
        value: UserPreferences[K]
    ) => {
        state[key] = value;
        savePreferences();
    };

    /**
     * Gets a preference value
     *
     * @param key - The preference key to retrieve
     * @returns The preference value or its default if not set
     */
    const getPreference = <K extends keyof UserPreferences>(
        key: K
    ): UserPreferences[K] => {
        // Use the value from state or fall back to the default value for this key
        return state[key] !== undefined ? state[key] : DEFAULT_PREFERENCES[key];
    };

    return {
        preferences: readonly(state),
        setPreference,
        getPreference,
        keys: USER_PREFERENCES_KEYS,
    };
}
